import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImpulseSpinner } from 'react-spinners-kit';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useReviewWorkspaceRole, useWorkspace } from 'hooks/useWorkspace';
import cn from 'classnames';

import backArrowGray from 'assets/backArrowGray.svg';
import BeaconLogo from 'assets/BeaconLogo.svg';
import tempImage from 'assets/tempImage.jpg';

import {
  getDesignReviewCountsAction,
  getDesignReviewHistoryAction,
  getReviewDataAction,
} from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';
import { setUserProfile } from 'Redux/Actions/userActions';
import actionTypes from 'Redux/constants';

import { ROUTES } from 'routes/route.constants';

import { getAiContextSuggestions, getAiContextWithAnalytics } from 'services/aiContextSuggestions';
import FilesService from 'services/files';
import { createPersona, generatePersona, listPersonas } from 'services/personaService';
import TeamDashboardReviews, { getDesignReviewJobStatus, getSingleDesignReview } from 'services/teamDashboardReviews';

import { DESIGN_AUDIT_TYPES, DESIGN_REVIEW_STATUS, PAGE_SIZE, PLATFORM } from 'utlis/constants';
import { errorReactToastify, successReactToastify } from 'utlis/toasts';
import { isFigmaURL } from 'utlis/validators';

import CategoryDetailView from './components/CategoryDetailView';
import { MIN_PERSONA_INPUT_LENGTH } from './components/constants';
import ContextModal from './components/ContextModal';
import CreatePersonaModal from './components/CreatePersonaModal';
import FlowSelection from './components/FlowSelection';
import ProductLibrarySelection from './components/ProductLibrarySelection';
import ReviewContext from './components/ReviewContext';
import ReviewResults from './components/ReviewResults';
import ShareReviewModal from './components/ShareReviewModal';
import styles from './components/styles.module.scss';
import UploadScreens from './components/UploadScreens';
import useDownloadPdfReport from './hooks/useDownloadPdfReport';
import {
  SIMULATE_REVIEW_COMPLETE,
  SIMULATED_CATEGORY,
  SIMULATED_PROBLEM_TAGS,
  SIMULATED_PRODUCT_DESCRIPTION,
  SIMULATED_REVIEW_DATA,
  SIMULATED_SCREENSHOT_UPLOADS,
} from './simulationConfig';

const PAGE_TYPES = {
  FLOW_SELECTION: 'flow selection',
  PRODUCT_LIBRARY_SELECTION: 'product library selection',
  UPLOAD_SCREENS: 'upload screens',
  CONTEXT_ADDITION: 'context addition',
};

const CreateReview = ({ onClose, existingReviewData, isPublicView = false }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { reviewId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedWorkspace, isViewer, isLoadingWorkspaces } = useWorkspace();

  useEffect(() => {
    if (isViewer && !reviewId && !isPublicView) {
      navigate(ROUTES.TEAM_HOME, { replace: true });
    }
  }, [isViewer, reviewId, isPublicView, navigate]);

  const authUser = useSelector(state => state.userStore);
  const userProfile = authUser;

  const [selectedCategoryForDetail, setSelectedCategoryForDetail] = useState(null);
  const [currentPage, setCurrentPage] = useState(PAGE_TYPES.FLOW_SELECTION);
  const [selectedCategory, setSelectedCategory] = useState('ux_critique');
  const [completedReviewData, setCompletedReviewData] = useState(null);
  const [selectedProblemChips, setSelectedProblemChips] = useState([]);
  const [showCategoryDetail, setShowCategoryDetail] = useState(false);
  const [showReviewResults, setShowReviewResults] = useState(false);
  const [productDescription, setProductDescription] = useState('');
  const [isReviewComplete, setIsReviewComplete] = useState(false);
  const [, setUploadingImages] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showContextModal, setShowContextModal] = useState(false);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [originalFiles, setOriginalFiles] = useState([]);
  const [reviewData, setReviewData] = useState(null);
  const [figmaUrl, setFigmaUrl] = useState('');
  const [figmaFramesData, setFigmaFramesData] = useState(null);
  const [title, setTitle] = useState('');
  const [audience, setAudience] = useState('');
  const [businessGoal, setBusinessGoal] = useState('');
  const [observedAnalytics, setObservedAnalytics] = useState('');
  const [isLoadingReview, setIsLoadingReview] = useState(false);
  const [urlReviewData, setUrlReviewData] = useState(null);

  // AI context pre-fill state
  const [aiSuggestions, setAiSuggestions] = useState(null);
  const [aiFieldStates, setAiFieldStates] = useState(null);
  const [isLoadingAiSuggestions, setIsLoadingAiSuggestions] = useState(false);
  const aiSuggestionsAbortControllerRef = useRef(null);

  // Persona state
  const [productName, setProductName] = useState('');
  const [personas, setPersonas] = useState([]);
  const [isPersonaModalOpen, setIsPersonaModalOpen] = useState(false);
  const [generatedPersonaData, setGeneratedPersonaData] = useState(null);
  const [isGeneratingPersona, setIsGeneratingPersona] = useState(false);
  const [isSavingPersona, setIsSavingPersona] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const previousAudienceRef = useRef('');
  const personaAbortControllerRef = useRef(null);

  // Analytics upload state
  const [analyticsUploadState, setAnalyticsUploadState] = useState(null); // null | 'uploading' | 'in-progress' | 'done'
  const [analyticsFiles, setAnalyticsFiles] = useState(null); // [{ name, previewUrl }] for UI display
  const [analyticsAttachment, setAnalyticsAttachment] = useState(null); // [{ url, type, mime_type }] for API
  // Ref so the basic AI suggestion effect always picks up the latest analytics URLs
  const analyticsS3UrlRef = useRef(null); // stores string[] for use in effects
  const analyticsFilesRef = useRef(null); // tracks current display files for unmount cleanup

  // Product library flow state
  const [selectedLibraryFlowUuid, setSelectedLibraryFlowUuid] = useState(null);
  const [selectedLibraryFlowData, setSelectedLibraryFlowData] = useState(null);

  const jobPollingIntervalRef = useRef(null);
  const latestSubmissionPayloadRef = useRef(null);
  const jobIdRef = useRef(null);
  const processedReviewUuidRef = useRef(null);
  const videoFrameBlobsRef = useRef(null);
  // Delayed timers scheduled from submit/failure handlers — tracked so they can be
  // cleared on unmount (otherwise they fire after the component is gone and navigate away).
  const submitPollTimeoutRef = useRef(null);
  const failureTimeoutRef = useRef(null);
  // Guards async callbacks (job polling) from running navigation after unmount.
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Only populate if userStore is empty but localStorage has userObject
    if (!authUser || !authUser.uuid) {
      try {
        const userObjectString = localStorage.getItem('userObject');

        if (userObjectString) {
          const userObject = JSON.parse(userObjectString);

          if (userObject && userObject.uuid) {
            dispatch({
              type: actionTypes.SIGN_IN_USER,
              payload: userObject,
            });
          }
        }
      }
      catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [authUser, dispatch]);

  // Fetch review credit data on mount (needed for direct URL access / page refresh)
  useEffect(() => {
    if (authUser?.uuid) {
      dispatch(getReviewDataAction());
    }
  }, [authUser, dispatch]);

  // Fetch workspace personas on mount
  useEffect(() => {
    if (selectedWorkspace?.uuid) {
      listPersonas(selectedWorkspace.uuid)
        .then(data => setPersonas(Array.isArray(data) ? data : []))
        .catch(() => setPersonas([]));
    }
  }, [selectedWorkspace?.uuid]);

  // Prefetch video frame blobs as soon as they're available so submit is instant
  useEffect(() => {
    const videoFrames = uploadedImages.filter(img => img.isVideoFrame);

    if (videoFrames.length === 0) {
      videoFrameBlobsRef.current = null;

      return;
    }

    Promise.all(
      videoFrames.map(async (img, index) => {
        const response = await fetch(img.url || img.preview);
        const blob = await response.blob();
        const ext = blob.type ? blob.type.split('/')[1] : 'jpg';

        return new File([blob], `frame_${index + 1}.${ext}`, { type: blob.type || 'image/jpeg' });
      })
    ).then(files => {
      videoFrameBlobsRef.current = files;
    })
      .catch(() => {
        videoFrameBlobsRef.current = null;
      });
  }, [uploadedImages]);

  // Trigger AI context pre-fill when images change.
  // Cancels any in-flight request and starts a fresh one whenever uploadedImages changes.
  useEffect(() => {
    // Skip for existing reviews — they already have context
    if (existingReviewData || urlReviewData) {
      return;
    }

    // Abort any in-flight infer_context request
    if (aiSuggestionsAbortControllerRef.current) {
      aiSuggestionsAbortControllerRef.current.abort();
      aiSuggestionsAbortControllerRef.current = null;
    }

    if (uploadedImages.length === 0) {
      // Images cleared — reset AI state so it re-fires on next upload
      setAiSuggestions(null);
      setAiFieldStates(null);
      setIsLoadingAiSuggestions(false);

      return;
    }

    const controller = new AbortController();

    aiSuggestionsAbortControllerRef.current = controller;

    const fetchSuggestions = async () => {
      setIsLoadingAiSuggestions(true);

      try {
        const imageUrls = uploadedImages
          .map(img => img.url || img.preview)
          .filter(Boolean);

        const isLibraryImages = uploadedImages.some(img => img.isLibraryImage);
        const lens = selectedCategory === 'growth-audit'
          ? 'growth'
          : selectedCategory === 'ux_critique'
            ? 'usability'
            : null;
        const suggestions = await getAiContextSuggestions(
          imageUrls,
          analyticsS3UrlRef.current,
          controller.signal,
          isLibraryImages,
          lens
        );

        setAiSuggestions(suggestions);
        setTitle(suggestions.title);
        setProductDescription(suggestions.problem);
        setAudience(suggestions.audience);
        setBusinessGoal(suggestions.businessGoal);
        setAiFieldStates({ title: 'ai', audience: 'ai', problem: 'ai', businessGoal: 'ai' });

        if (suggestions.productName) {
          setProductName(suggestions.productName);
        }
      }
      catch (err) {
        // Ignore abort errors — a new request is already in flight
        if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED') {
          return;
        }

        // Silently fail — user can fill in fields manually
      }
      finally {
        if (aiSuggestionsAbortControllerRef.current === controller) {
          aiSuggestionsAbortControllerRef.current = null;
          setIsLoadingAiSuggestions(false);
        }
      }
    };

    fetchSuggestions();
  }, [uploadedImages, existingReviewData, urlReviewData]);

  // Restore all fields to the last AI-suggested values
  const handleRestoreAiSuggestions = useCallback(() => {
    if (!aiSuggestions) {
      return;
    }

    setTitle(aiSuggestions.title);
    setProductDescription(aiSuggestions.problem);
    setAudience(aiSuggestions.audience);
    setBusinessGoal(aiSuggestions.businessGoal);
    setSelectedPersona(null);

    const newFieldStates = { title: 'ai', audience: 'ai', problem: 'ai', businessGoal: 'ai' };

    if (aiSuggestions.observedAnalytics) {
      setObservedAnalytics(aiSuggestions.observedAnalytics);
      newFieldStates.observedAnalytics = 'ai';
    }

    setAiFieldStates(newFieldStates);
  }, [aiSuggestions]);

  // Mark a single field as user-edited (loses AI purple styling)
  const handleFieldEdit = useCallback(field => {
    if (!aiSuggestions) {
      return;
    }

    setAiFieldStates(prev => ({ ...prev, [field]: 'user' }));
  }, [aiSuggestions]);

  // Restore AI-inferred value for a single field
  const handleRestoreFieldAi = useCallback(field => {
    if (!aiSuggestions) {
      return;
    }

    switch (field) {
      case 'title':
        setTitle(aiSuggestions.title);
        break;
      case 'audience':
        setAudience(aiSuggestions.audience);
        setSelectedPersona(null);
        break;
      case 'problem':
        setProductDescription(aiSuggestions.problem);
        break;
      case 'businessGoal':
        setBusinessGoal(aiSuggestions.businessGoal);
        break;
      default:
        return;
    }

    setAiFieldStates(prev => ({ ...prev, [field]: 'ai' }));
  }, [aiSuggestions]);

  // Upload analytics images (up to 10) to S3 then call analytics enrichment endpoint
  const handleAnalyticsUpload = useCallback(async fileArray => {
    // Build display data with local preview URLs immediately
    const displayFiles = fileArray.map(file => ({
      name: file.name,
      previewUrl: URL.createObjectURL(file),
    }));

    setAnalyticsUploadState('uploading');
    setAnalyticsFiles(displayFiles);

    try {
      // Upload all files to S3 in parallel
      const s3Urls = await Promise.all(
        fileArray.map(file => FilesService.uploadFileFromLocal(file))
      );

      analyticsS3UrlRef.current = s3Urls;
      setAnalyticsAttachment(
        s3Urls.map((url, i) => ({ url, type: 'image', mime_type: fileArray[i].type }))
      );

      // Move to analyzing state while AI enriches context with analytics
      setAnalyticsUploadState('in-progress');
      setIsLoadingAiSuggestions(true);

      // Use the new analytics enrichment endpoint
      const enrichedContext = await getAiContextWithAnalytics(
        {
          title,
          audience,
          problem: productDescription,
          businessGoal,
        },
        s3Urls,
        null
      );

      // Update all context fields with enriched data, preserving existing values when enrichment returns empty
      const mergedContext = {
        title: enrichedContext.title || title,
        problem: enrichedContext.problem || productDescription,
        audience: enrichedContext.audience || audience,
        businessGoal: enrichedContext.businessGoal || businessGoal,
        observedAnalytics: enrichedContext.observedAnalytics || '',
      };

      setAiSuggestions(mergedContext);
      setTitle(mergedContext.title);
      setProductDescription(mergedContext.problem);
      setAudience(mergedContext.audience);
      setBusinessGoal(mergedContext.businessGoal);
      setObservedAnalytics(mergedContext.observedAnalytics);
      setAiFieldStates({
        title: 'ai',
        audience: 'ai',
        problem: 'ai',
        businessGoal: 'ai',
        observedAnalytics: 'ai',
      });
      setAnalyticsUploadState('done');
    }
    catch {
      // Silently revert to empty state on error
      analyticsS3UrlRef.current = null;
      setAnalyticsUploadState(null);
      displayFiles.forEach(f => URL.revokeObjectURL(f.previewUrl));
      setAnalyticsFiles(null);
      setAnalyticsAttachment(null);
    }
    finally {
      setIsLoadingAiSuggestions(false);
    }
  }, [title, audience, productDescription, businessGoal]);

  // Remove all analytics files — does not re-run AI, fields keep their current values
  const handleAnalyticsRemove = useCallback(() => {
    if (analyticsFiles) {
      analyticsFiles.forEach(f => URL.revokeObjectURL(f.previewUrl));
    }

    analyticsS3UrlRef.current = null;
    setAnalyticsUploadState(null);
    setAnalyticsFiles(null);
    setAnalyticsAttachment(null);
    setObservedAnalytics('');
  }, [analyticsFiles]);

  const handleCreatePersona = useCallback(async () => {
    if (!audience.trim() || audience.trim().length < MIN_PERSONA_INPUT_LENGTH) {
      return;
    }

    if (personaAbortControllerRef.current) {
      personaAbortControllerRef.current.abort();
    }

    const controller = new AbortController();

    personaAbortControllerRef.current = controller;
    setIsGeneratingPersona(true);

    try {
      const result = await generatePersona(audience, productName || undefined, controller.signal);

      if (controller.signal.aborted) {
        return;
      }

      if (result.status === 'success' && result.persona) {
        setGeneratedPersonaData(result.persona);
        setIsPersonaModalOpen(true);
      }
    }
    catch (err) {
      if (err?.name === 'AbortError' || err?.code === 'ERR_CANCELED' || controller.signal.aborted) {
        return;
      }

      errorReactToastify(
        'Failed to generate persona. Please try again.',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '470px' },
        }
      );
    }
    finally {
      if (personaAbortControllerRef.current === controller) {
        personaAbortControllerRef.current = null;
        setIsGeneratingPersona(false);
      }
    }
  }, [audience, productName]);

  const handleSavePersona = useCallback(async editedName => {
    if (!generatedPersonaData || !selectedWorkspace?.uuid) {
      return;
    }

    setIsSavingPersona(true);

    try {
      const savedPersona = await createPersona(selectedWorkspace.uuid, {
        name: editedName,
        standardSummary: generatedPersonaData.standard,
        expandedRecord: generatedPersonaData.expanded,
        sourceInput: audience,
      });

      setPersonas(prev => [savedPersona, ...prev]);

      // Brief pause to show saving state before closing
      await new Promise(resolve => {
        setTimeout(resolve, 1500);
      });

      setIsPersonaModalOpen(false);
      setGeneratedPersonaData(null);

      successReactToastify(
        'Persona saved to workspace',
        {
          autoClose: 3000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#ECFDF5', width: '470px' },
        }
      );
    }
    catch {
      errorReactToastify(
        'Failed to save persona. Please try again.',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '470px' },
        }
      );
    }
    finally {
      setIsSavingPersona(false);
    }
  }, [generatedPersonaData, selectedWorkspace?.uuid]);

  // Select an existing persona from the dropdown
  const handleSelectPersona = useCallback(persona => {
    previousAudienceRef.current = audience;
    setSelectedPersona(persona);
    setAudience(persona.standard_summary);
    setAiFieldStates(prev => (prev ? { ...prev, audience: 'user' } : prev));
  }, [audience]);

  // Deselect persona and restore previous audience content
  const handleDeselectPersona = useCallback(() => {
    setAudience(previousAudienceRef.current);
    setSelectedPersona(null);
  }, []);

  // Use existingReviewData if provided, otherwise use urlReviewData
  const effectiveReviewData = useMemo(
    () => existingReviewData || urlReviewData,
    [existingReviewData, urlReviewData]
  );

  const {
    isGenerating: isGeneratingPdf, generateAndDownload: handleDownloadPdf,
  } = useDownloadPdfReport(effectiveReviewData);

  const getCategoryConfig = categoryKey => {
    switch (categoryKey) {
      case 'onboarding_flow':
        return {
          primingOption: 'onboarding-flow',
          templateLabel: 'User Onboarding',
          problem: '',
          showDeeperInsights: true,
        };
      case 'new_ideas':
        return {
          primingOption: 'new-ideas',
          templateLabel: 'New Ideas',
          problem: '',
          showDeeperInsights: true,
        };
      case 'growth-audit':
        return {
          primingOption: 'growth-audit',
          templateLabel: 'Growth Audit',
          problem: '',
          showDeeperInsights: true,
        };
      case 'ux_critique':
      default:
        return {
          primingOption: 'ux-critique',
          templateLabel: 'UX Critique',
          problem: '',
          showDeeperInsights: true,
        };
    }
  };

  // Helper function to determine if Share button should be shown
  const showShareButton = useMemo(() => (
    (reviewData && reviewData.review_status === DESIGN_REVIEW_STATUS.DONE && reviewData.uuid)
      || (
        completedReviewData
          && completedReviewData.review_status === DESIGN_REVIEW_STATUS.DONE
          && completedReviewData.uuid
      )
  ), [reviewData, completedReviewData]);

  // Helper function to get the review UUID for sharing
  const getReviewUuid = useMemo(() => (
    reviewData?.uuid || completedReviewData?.uuid || effectiveReviewData?.uuid
  ), [reviewData, completedReviewData, effectiveReviewData]);

  // Role the current user holds in the workspace that owns the loaded review
  const { isReviewOwner, isReviewViewer } = useReviewWorkspaceRole(effectiveReviewData);

  const buildGenerateReviewFormData = ({
    imageFiles = [],
    imageUrls = [],
    problemText = '',
    figmaUserId = null,
    category = 'ux_critique',
    figmaUrl: submittedFigmaUrl = '',
    audience: submittedAudience = '',
    businessGoals: submittedBusinessGoals = '',
    analyticsAttachments = [],
    productLibraryData = null,
    observedAnalyticsText = '',
    persona = null,
  }) => {
    const { primingOption, templateLabel, problem, showDeeperInsights } = getCategoryConfig(category);

    const userProblem = [problemText, problem]
      .map(p => (p ?? '').toString().trim())
      .filter(Boolean)
      .join('\n\n');

    const formData = new FormData();

    formData.append('figma_user_id', figmaUserId || '');
    formData.append('page_name', 'Screenshots Page');
    formData.append('file_name', title || '');
    formData.append('review_frame_id', '0:0');
    formData.append('review_frame_name', title || '');
    formData.append('priming_options', primingOption);
    formData.append('template', templateLabel);
    formData.append('user_problem', userProblem || '');
    formData.append('accessibility_guideline_type', null);
    formData.append('accessibility_guideline_name', null);
    formData.append('brand_guidelines', null);
    formData.append('user_platform', 'web_dashboard');
    formData.append('design_type', DESIGN_AUDIT_TYPES.SCREENSHOT);
    formData.append('audience', submittedAudience || '');
    formData.append('business_goal', submittedBusinessGoals || '');
    formData.append('show_deeper_insights', showDeeperInsights);

    if (observedAnalyticsText) {
      formData.append('observed_analytics', observedAnalyticsText);
    }

    const count = imageUrls.length > 0 ? imageUrls.length : imageFiles.length;

    formData.append('review_frame_ids', JSON.stringify(Array(count).fill('0.0')));
    formData.append('review_frame_names', JSON.stringify(
      Array.from({ length: count }, (_, index) => `Screen ${index + 1}`)
    ));

    if (imageUrls.length > 0) {
      imageUrls.forEach((url, index) => {
        formData.append(`frame_url_${index}`, url);
        formData.append(`frame_json_${index}`, JSON.stringify({}));
      });
    }
    else {
      imageFiles.forEach((file, index) => {
        formData.append(`frame_image_${index}`, file);
        formData.append(`frame_json_${index}`, JSON.stringify({}));
      });
    }

    if (submittedFigmaUrl) {
      formData.append('figma_url', submittedFigmaUrl);
    }

    if (analyticsAttachments.length > 0) {
      formData.append('analytics_attachments', JSON.stringify(analyticsAttachments));
    }

    if (productLibraryData) {
      formData.append('product_library_flow', JSON.stringify({
        flow_uuid: productLibraryData.flow_uuid,
        flow_version: productLibraryData.flow_version,
      }));
    }

    if (persona?.name) {
      formData.append('persona_name', persona.name);
    }

    if (persona?.uuid || persona?.id) {
      formData.append('persona_id', persona.uuid || persona.id);
    }

    return formData;
  };

  const downloadFrameImages = useCallback(async frameList => {
    const blobs = await Promise.all(
      frameList.map(async frame => {
        const imageUrl = frame.image_url || frame.imageUrl;

        if (!imageUrl) {
          throw new Error(`Missing image_url for frame: ${frame.frame_id || frame.frame_name}`);
        }

        const response = await fetch(imageUrl);

        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${imageUrl}`);
        }

        return response.blob();
      })
    );

    return blobs;
  }, []);

  // Build FormData for generate_review API when source is Figma frames
  const buildFigmaReviewFormData = useCallback(({
    imageBlobs = [],
    frameList = [],
    sectionName = '',
    fileName = '',
    reviewFrameName = '',
    figmaUserId = '',
    primingOption = 'ux-critique',
    templateLabel = 'UX Critique',
    userProblem = '',
    audienceVal = '',
    businessGoalVal = '',
    showDeeperInsights = false,
    analyticsAttachments = [],
    observedAnalyticsText = '',
    persona = null,
  }) => {
    const formData = new FormData();

    imageBlobs.forEach((blob, index) => {
      formData.append(`frame_image_${index}`, blob);
      formData.append(`frame_json_${index}`, JSON.stringify({}));
    });

    formData.append('figma_user_id', figmaUserId || '');
    formData.append('page_name', sectionName || '');
    formData.append('file_name', fileName || '');
    formData.append('review_frame_id', frameList[0]?.frame_id ?? '0:0');
    formData.append('review_frame_ids', JSON.stringify(frameList.map(f => f.frame_id)));
    formData.append('review_frame_name', reviewFrameName || (frameList[0]?.frame_name ?? ''));
    formData.append('review_frame_names', JSON.stringify(frameList.map(f => f.frame_name)));
    formData.append('priming_options', primingOption);
    formData.append('template', templateLabel);
    formData.append('user_problem', userProblem || '');
    formData.append('audience', audienceVal || '');
    formData.append('business_goal', businessGoalVal || '');
    formData.append('user_platform', 'web_dashboard');
    formData.append('design_type', DESIGN_AUDIT_TYPES.FIGMA_FRAME);
    formData.append('show_deeper_insights', showDeeperInsights);
    formData.append('accessibility_guideline_type', null);
    formData.append('accessibility_guideline_name', null);
    formData.append('brand_guidelines', null);

    if (analyticsAttachments.length > 0) {
      formData.append('analytics_attachments', JSON.stringify(analyticsAttachments));
    }

    if (observedAnalyticsText) {
      formData.append('observed_analytics', observedAnalyticsText);
    }

    if (persona?.name) {
      formData.append('persona_name', persona.name);
    }

    if (persona?.uuid || persona?.id) {
      formData.append('persona_id', persona.uuid || persona.id);
    }

    return formData;
  }, []);

  const stopJobPolling = useCallback(() => {
    if (jobPollingIntervalRef.current) {
      clearInterval(jobPollingIntervalRef.current);
      jobPollingIntervalRef.current = null;
    }
    jobIdRef.current = null;
  }, []);

  const isFormValid = useMemo(
    () => title.trim() !== ''
      && uploadedImages.length > 0,
    [title, uploadedImages]
  );

  const openFindingsView = useCallback(record => {
    if (record) {
      dispatch(setCurrentRecord(record));
    }

    setSelectedCategoryForDetail('ALL');
    setShowCategoryDetail(true);
  }, [dispatch]);

  const handleJobStatusResponse = useCallback(async statusResponse => {
    // The component may have unmounted (user closed the review) while a poll was in
    // flight or scheduled — bail out so we never navigate away from wherever they are now.
    if (!isMountedRef.current) {
      stopJobPolling();

      return;
    }

    const reviewStatus = typeof statusResponse?.review_status === 'number'
      ? statusResponse.review_status
      : null;

    if (Array.isArray(statusResponse?.final_feedback)) {
      setReviewData(statusResponse);
    }

    if (reviewStatus === DESIGN_REVIEW_STATUS.DONE) {
      stopJobPolling();

      setCompletedReviewData(statusResponse);

      setIsReviewComplete(true);
      setShowReviewResults(true);
      openFindingsView(statusResponse);

      // Navigate to review URL if we have a UUID and we're on the create route
      if (statusResponse?.uuid && !reviewId) {
        navigate(ROUTES.TEAM_REVIEWS_DETAIL(statusResponse.uuid), { replace: true });
      }

      await fetchDesignAuditHistory();
    }

    if (reviewStatus === DESIGN_REVIEW_STATUS.FAILURE) {
      stopJobPolling();

      errorReactToastify(
        'The review has failed, please try again later',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '470px' },
        }
      );

      failureTimeoutRef.current = setTimeout(() => {
        failureTimeoutRef.current = null;

        if (!isMountedRef.current) {
          return;
        }

        if (onClose) {
          onClose();
        }
        else {
          navigate(ROUTES.TEAM_HOME);
        }
      }, 5000);
    }
  }, [fetchDesignAuditHistory, onClose, stopJobPolling, navigate, reviewId, openFindingsView]);

  const fetchJobStatus = useCallback(async activeJobId => {
    if (!activeJobId) {
      return;
    }

    try {
      const statusResponse = await getDesignReviewJobStatus(activeJobId);

      await handleJobStatusResponse(statusResponse);
    }
    catch (error) {
      Sentry.captureException(error);
    }
  }, [handleJobStatusResponse]);

  const startJobPolling = useCallback(activeJobId => {
    if (!activeJobId) {
      return;
    }

    if (jobIdRef.current === activeJobId && jobPollingIntervalRef.current) {
      return;
    }

    stopJobPolling();
    jobIdRef.current = activeJobId;

    fetchJobStatus(activeJobId);

    jobPollingIntervalRef.current = setInterval(() => {
      fetchJobStatus(activeJobId);
    }, 10000);
  }, [fetchJobStatus, stopJobPolling]);

  useEffect(() => {
    if (authUser && authUser.uuid) {
      dispatch(setUserProfile(authUser));
    }
  }, [authUser?.uuid, dispatch]);

  useEffect(() => {
    if (effectiveReviewData) {
      return;
    }

    const figmaConnected = searchParams.get('figmaConnected');

    if (figmaConnected === 'true') {
      // Refresh user profile to get updated Figma token
      if (authUser && authUser.uuid) {
        dispatch(setUserProfile(authUser, null, true));
      }

      setCurrentPage(PAGE_TYPES.UPLOAD_SCREENS);

      searchParams.delete('figmaConnected');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, effectiveReviewData, authUser, dispatch]);

  // Fetch review data from URL if reviewId is present
  useEffect(() => {
    const fetchReviewFromUrl = async () => {
      // Only fetch if reviewId exists and we don't have existingReviewData or urlReviewData
      if (reviewId && !existingReviewData && !urlReviewData) {
        setIsLoadingReview(true);

        try {
          const fetchedReviewData = await getSingleDesignReview(reviewId);

          if (fetchedReviewData && typeof fetchedReviewData === 'object' && fetchedReviewData.uuid) {
            processedReviewUuidRef.current = null;
            setUrlReviewData(fetchedReviewData);
            dispatch(setCurrentRecord(fetchedReviewData));
          }
          else {
            // Review not found, navigate back
            navigate(ROUTES.TEAM_HOME);
            errorReactToastify(
              'This audit does not exist or you don\'t have permission to view it.',
              {
                autoClose: 5000,
                position: 'bottom-center',
                hideProgressBar: true,
                style: { backgroundColor: '#FDF0F0', width: '530px' },
              }
            );
          }
        }
        catch (error) {
          // Navigate back on error
          navigate(ROUTES.TEAM_HOME);
          errorReactToastify(
            'This audit does not exist or you don\'t have permission to view it.',
            {
              autoClose: 5000,
              position: 'bottom-center',
              hideProgressBar: true,
              style: { backgroundColor: '#FDF0F0', width: '530px' },
            }
          );
        }
        finally {
          setIsLoadingReview(false);
        }
      }
    };

    fetchReviewFromUrl();
  }, [reviewId, existingReviewData, urlReviewData, navigate, dispatch]);

  // Handle existing review data
  useEffect(() => {
    const reviewUuid = effectiveReviewData?.uuid;

    // Reset processed UUID if the review UUID has changed
    if (reviewUuid && processedReviewUuidRef.current !== reviewUuid) {
      processedReviewUuidRef.current = null;
    }

    if (effectiveReviewData && reviewUuid && processedReviewUuidRef.current !== reviewUuid) {
      processedReviewUuidRef.current = reviewUuid;

      // Set review data and populate form
      setReviewData(effectiveReviewData);

      // Set current page to CONTEXT_ADDITION for existing reviews
      setCurrentPage(PAGE_TYPES.CONTEXT_ADDITION);

      // Completed audits skip the intermediate screen and open findings directly
      if (effectiveReviewData.review_status === DESIGN_REVIEW_STATUS.DONE) {
        setIsReviewComplete(true);
        setShowReviewResults(true);
        openFindingsView(effectiveReviewData);
      }
      else if (effectiveReviewData.review_status === DESIGN_REVIEW_STATUS.FAILURE) {
        setShowReviewResults(true);
      }
      else if (effectiveReviewData.review_status > DESIGN_REVIEW_STATUS.DONE) {
        const createdAt = new Date(effectiveReviewData.created_at);
        const now = new Date();
        const maxProcessingTime = 20 * 60 * 1000;

        if ((now - createdAt) < maxProcessingTime) {
          startJobPolling(effectiveReviewData.firestore_job_id);
        }
        setShowReviewResults(true);
      }

      // Set uploaded images from design_images
      if (effectiveReviewData.extra_data?.frame_images) {
        const prefixUrl = process.env.REACT_APP_FIREBASE_STORAGE_PREFIX;
        const postfixUrl = '?alt=media';

        const formattedImages = effectiveReviewData.extra_data.frame_images.map((imageUrl, index) => ({
          name: `Review Image ${index + 1}`,
          uid: `existing-${index}`,
          preview: `${prefixUrl}${encodeURIComponent(imageUrl)}${postfixUrl}`,
          url: `${prefixUrl}${encodeURIComponent(imageUrl)}${postfixUrl}`,
          progress: 100,
          isProgressLoading: false,
          isExistingImage: true,
        }));

        setUploadedImages(formattedImages);
      }
      else if (effectiveReviewData.review_status > DESIGN_REVIEW_STATUS.DONE) {
        // eslint-disable-next-line no-unused-vars
        const formattedImages = effectiveReviewData.extra_data.review_frame_names.map((_, index) => ({
          name: `Review Image ${index + 1}`,
          uid: `existing-${index}`,
          preview: tempImage,
          url: tempImage,
          progress: 100,
          isProgressLoading: false,
          isExistingImage: true,
        }));

        setUploadedImages(formattedImages);
      }

      if (effectiveReviewData.audit_data?.category || effectiveReviewData.audit_data?.review_category) {
        setSelectedCategory(effectiveReviewData.audit_data.category || effectiveReviewData.audit_data.review_category);
      }

      const existingProblemTags = effectiveReviewData.audit_data?.problem_tags
        || effectiveReviewData.audit_data?.problemTags;

      if (Array.isArray(existingProblemTags)) {
        setSelectedProblemChips(existingProblemTags);
      }
    }

    // Reset processed UUID if effectiveReviewData becomes null/undefined
    else if (!effectiveReviewData) {
      processedReviewUuidRef.current = null;
    }
  }, [effectiveReviewData, startJobPolling, openFindingsView]);

  // Restore TeamDashboard fields from localStorage when component mounts (only for new reviews)
  useEffect(() => {
    if (effectiveReviewData) {
      return;
    } // Skip for existing reviews

    const storedTitle = localStorage.getItem('teamReview_title');
    const storedProductDescription = localStorage.getItem('teamReview_productDescription');
    const storedAudience = localStorage.getItem('teamReview_audience');
    const storedBusinessGoal = localStorage.getItem('teamReview_businessGoal');
    const storedSelectedCategory = localStorage.getItem('teamReview_selectedCategory');
    const storedSelectedProblemChips = localStorage.getItem('teamReview_selectedProblemChips');

    if (storedTitle) {
      setTitle(storedTitle || '');
      localStorage.removeItem('teamReview_title');
    }

    if (storedProductDescription) {
      setProductDescription(storedProductDescription || '');
      localStorage.removeItem('teamReview_productDescription');
    }

    if (storedAudience) {
      setAudience(storedAudience || '');
      localStorage.removeItem('teamReview_audience');
    }

    if (storedBusinessGoal) {
      setBusinessGoal(storedBusinessGoal || '');
      localStorage.removeItem('teamReview_businessGoal');
    }

    if (storedSelectedCategory) {
      setSelectedCategory(storedSelectedCategory);
      localStorage.removeItem('teamReview_selectedCategory');
    }

    if (storedSelectedProblemChips) {
      try {
        const parsedChips = JSON.parse(storedSelectedProblemChips);

        if (Array.isArray(parsedChips)) {
          setSelectedProblemChips(parsedChips);
        }
      }
      catch (error) {
        Sentry.captureException(error);
      }
      localStorage.removeItem('teamReview_selectedProblemChips');
    }
  }, []);

  const shouldSimulateCompletedState = SIMULATE_REVIEW_COMPLETE && !existingReviewData;

  useEffect(() => {
    if (!shouldSimulateCompletedState) {
      return;
    }

    // Skip flow selection for simulation
    setCurrentPage(PAGE_TYPES.CONTEXT_ADDITION);
    setShowReviewResults(true);
    setIsReviewComplete(true);
    setReviewData(SIMULATED_REVIEW_DATA);
    openFindingsView(SIMULATED_REVIEW_DATA);
    setProductDescription(SIMULATED_PRODUCT_DESCRIPTION || '');
    setTitle(SIMULATED_REVIEW_DATA?.title || '');
    setSelectedCategory(SIMULATED_CATEGORY || 'ux_critique');
    setSelectedProblemChips(Array.isArray(SIMULATED_PROBLEM_TAGS) ? SIMULATED_PROBLEM_TAGS : []);
    setUploadedImages(SIMULATED_SCREENSHOT_UPLOADS);
  }, [
    shouldSimulateCompletedState, openFindingsView,
  ]);
  // Keep analyticsFilesRef in sync so the unmount cleanup always has the latest value
  useEffect(() => {
    analyticsFilesRef.current = analyticsFiles;
  }, [analyticsFiles]);

  // Cleanup polling, pending timers, and analytics preview URLs on component unmount
  useEffect(() => () => {
    isMountedRef.current = false;

    if (jobPollingIntervalRef.current) {
      clearInterval(jobPollingIntervalRef.current);
      jobPollingIntervalRef.current = null;
    }
    // Clear timers scheduled from submit/failure handlers so they can't fire (and
    // navigate) after the component is gone.
    if (submitPollTimeoutRef.current) {
      clearTimeout(submitPollTimeoutRef.current);
      submitPollTimeoutRef.current = null;
    }
    if (failureTimeoutRef.current) {
      clearTimeout(failureTimeoutRef.current);
      failureTimeoutRef.current = null;
    }
    analyticsFilesRef.current?.forEach(f => URL.revokeObjectURL(f.previewUrl));
  }, []);

  // Handle flow selection
  const handleFlowSelect = flowId => {
    setSelectedCategory(flowId);
  };

  const handleFlowNext = () => {
    setCurrentPage(PAGE_TYPES.PRODUCT_LIBRARY_SELECTION);
  };

  const handleLibraryFlowConfirm = useCallback((flow, preBuiltImages) => {
    let libraryImages;

    if (preBuiltImages) {
      libraryImages = preBuiltImages;
    }
    else {
      libraryImages = (flow.assets || []).map((asset, index) => ({
        uid: `library-${flow.uuid}-${index}`,
        url: asset.image_url,
        preview: asset.thumbnail_url || asset.image_url,
        isLibraryImage: true,
        progress: 100,
        isProgressLoading: false,
      }));
    }

    // Store the flow data including version for extra_data
    setSelectedLibraryFlowData({
      flow_uuid: flow.uuid,
      flow_name: flow.name,
      flow_version: flow.selected_version || flow.current_version,
      category_name: flow.category_name,
    });

    setUploadedImages(libraryImages);
    setCurrentPage(PAGE_TYPES.CONTEXT_ADDITION);
  }, []);

  const handleUploadNext = () => {
    setCurrentPage(PAGE_TYPES.CONTEXT_ADDITION);
  };

  const handleUploadBack = () => {
    setCurrentPage(PAGE_TYPES.FLOW_SELECTION);
  };

  // Handle share modal
  const handleShareClick = () => {
    setShowShareModal(true);
  };

  const handleCloseShareModal = () => {
    setShowShareModal(false);
  };

  // Early return for viewers without reviewId
  if ((isLoadingWorkspaces || isViewer) && !reviewId && !isPublicView) {
    return null;
  }

  const isStageLayout
    = (currentPage === PAGE_TYPES.CONTEXT_ADDITION || isReviewComplete) && !showCategoryDetail;

  // Show loading state when on a review detail URL until we have review data
  const isDetailRouteWithoutData = reviewId && !existingReviewData && !urlReviewData;

  if (isDetailRouteWithoutData || isLoadingReview) {
    return (
      <div className="flex justify-center items-center min-h-screen flex-col bg-gray-50">
        <ImpulseSpinner frontColor={'#28BDAE'} />
      </div>
    );
  }

  return (
    <div className="relative w-full h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 flex items-center justify-between px-6 py-3 h-16">
        <div className="flex items-center gap-[16px]">
          {!isPublicView && (
            <button
              onClick={() => {
                if (personaAbortControllerRef.current) {
                  personaAbortControllerRef.current.abort();
                  personaAbortControllerRef.current = null;
                  setIsGeneratingPersona(false);
                }

                if (onClose) {
                  onClose();
                }
                else {
                  navigate(ROUTES.TEAM_HOME);
                }
              }}
              className="flex items-center justify-center bg-transparent border-none p-0 cursor-pointer"
            >
              <img src={backArrowGray} alt="Back" className="w-6 h-6" />
            </button>
          )}
          <span className="font-medium text-base leading-6 text-[#121723]">
            {title || effectiveReviewData?.extra_data?.review_frame_name || 'New Audit'}
          </span>
        </div>
        <div className="flex items-center gap-16">
          {showReviewResults && isReviewComplete && (
            <button
              type="button"
              className="flex flex-row items-center py-2 px-3 bg-white border border-[#E1E4E8]
              rounded-lg shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.04),0px_1px_1px_rgba(0,0,0,0.02)]
              hover:bg-gray-50"
              onClick={() => setShowContextModal(true)}
            >
              <span className="font-medium text-sm leading-5 text-[#1C2531]">
                Context
              </span>
            </button>
          )}
          {/* show 'Made with Beacon AI' button on the share page */}
          {isPublicView && showReviewResults && showShareButton && (
            <a
              href={process.env.REACT_APP_ONBEACON_WEBSITE_URL || location.origin}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-row items-center gap-8 bg-white border border-[#E1E4E8]
              rounded-lg px-5 py-2.5 shadow-[0px_2px_4px_-1px_rgba(0,0,0,0.04),0px_1px_1px_rgba(0,0,0,0.02)]"
            >
              <img src={BeaconLogo} alt="onBeacon Logo" className="w-5 h-5" />
              <span className="font-medium text-sm leading-5">
                Made with onBeacon
              </span>
            </a>
          )}
          {/* Share Button - owners of the review's workspace see all reviews; editors only see their own */}
          {!isPublicView
            && !isReviewViewer
            && (isReviewOwner || (authUser?.uuid && effectiveReviewData?.owner?.uuid === authUser.uuid))
            && showReviewResults
            && showShareButton && (
            <button
              onClick={handleShareClick}
              className="flex items-center justify-center px-5 py-2.5
              h-10 bg-primary-500 rounded-lg hover:bg-primary-600 transition-colors"
            >
              <span className="text-sm font-medium text-white">
                Share
              </span>
            </button>
          )}
        </div>
      </header>

      {/* Main Content Area */}
      <div
        className={cn(
          'flex-1 flex justify-center overflow-x-hidden min-h-0 overflow-y-scroll py-20',
          {
            [styles.mainContentDottedBg]:
              (showCategoryDetail && selectedCategoryForDetail) || isStageLayout,
          }
        )}
      >
        {currentPage === PAGE_TYPES.FLOW_SELECTION && !isReviewComplete && (
          <FlowSelection
            selectedFlow={selectedCategory}
            onFlowSelect={handleFlowSelect}
            onNext={handleFlowNext}
          />
        )}

        {currentPage === PAGE_TYPES.PRODUCT_LIBRARY_SELECTION && !isReviewComplete && (
          <ProductLibrarySelection
            onBack={() => setCurrentPage(PAGE_TYPES.FLOW_SELECTION)}
            onConfirm={handleLibraryFlowConfirm}
            selectedLibraryFlowUuid={selectedLibraryFlowUuid}
            onLibraryFlowSelect={setSelectedLibraryFlowUuid}
          />
        )}

        {currentPage === PAGE_TYPES.UPLOAD_SCREENS && !isReviewComplete && (
          <UploadScreens
            uploadedImages={uploadedImages}
            setUploadedImages={setUploadedImages}
            setOriginalFiles={setOriginalFiles}
            figmaUrl={figmaUrl}
            setFigmaUrl={setFigmaUrl}
            setFigmaFramesData={setFigmaFramesData}
            onNext={handleUploadNext}
            onBack={handleUploadBack}
            authUser={authUser}
            userProfile={userProfile}
            title={title}
            productDescription={productDescription}
            audience={audience}
            businessGoal={businessGoal}
            selectedCategory={selectedCategory}
            selectedProblemChips={selectedProblemChips}
          />
        )}

        {(currentPage === PAGE_TYPES.CONTEXT_ADDITION || isReviewComplete) && !showCategoryDetail && (
          showReviewResults && !isSubmitting
            ? (
              <ReviewResults
                reviewData={reviewData}
                title={title}
                productDescription={productDescription}
                audience={audience}
                businessGoal={businessGoal}
              />
            )
            : (
              <ReviewContext
                handleSubmitReview={handleSubmitReview}
                isFormValid={isFormValid}
                productDescription={productDescription}
                setProductDescription={setProductDescription}
                isSubmitting={isSubmitting}
                title={title}
                setTitle={setTitle}
                audience={audience}
                setAudience={setAudience}
                businessGoal={businessGoal}
                goToPrevPage={goToPrevPage}
                setBusinessGoal={setBusinessGoal}
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
                selectedProblemChips={selectedProblemChips}
                setSelectedProblemChips={setSelectedProblemChips}
                aiFieldStates={aiFieldStates}
                isLoadingAiSuggestions={isLoadingAiSuggestions}
                hasAiSuggestions={Boolean(aiSuggestions)}
                onRestoreAiSuggestions={handleRestoreAiSuggestions}
                onFieldEdit={handleFieldEdit}
                onRestoreFieldAi={handleRestoreFieldAi}
                analyticsUploadState={analyticsUploadState}
                analyticsFiles={analyticsFiles}
                onAnalyticsUpload={handleAnalyticsUpload}
                onAnalyticsRemove={handleAnalyticsRemove}
                disabled={isLoadingAiSuggestions && !aiSuggestions}
                observedAnalytics={observedAnalytics}
                setObservedAnalytics={setObservedAnalytics}
                personas={personas}
                onCreatePersona={handleCreatePersona}
                onSelectPersona={handleSelectPersona}
                onDeselectPersona={handleDeselectPersona}
                selectedPersona={selectedPersona}
                isGeneratingPersona={isGeneratingPersona}
                isViewer={isViewer}
              />
            )
        )}

        {showCategoryDetail && selectedCategoryForDetail && (
          <CategoryDetailView
            category={selectedCategoryForDetail}
            isPublicView={isPublicView}
            onCategoryClick={category => {
              setSelectedCategoryForDetail(category);
            }}
          />
        )}
      </div>

      {getReviewUuid && (
        <ShareReviewModal
          isOpen={showShareModal}
          onClose={handleCloseShareModal}
          reviewUuid={getReviewUuid}
        />
      )}

      <ContextModal
        isOpen={showContextModal}
        onClose={() => setShowContextModal(false)}
        reviewData={reviewData}
        productDescription={productDescription}
        audience={audience}
        businessGoal={businessGoal}
        isReviewComplete={isReviewComplete}
        isGeneratingPdf={isGeneratingPdf}
        onDownloadPdf={handleDownloadPdf}
      />

      {isPersonaModalOpen && generatedPersonaData && (
        <CreatePersonaModal
          isOpen={isPersonaModalOpen}
          personaName={generatedPersonaData.persona_name || ''}
          standardSummary={generatedPersonaData.standard || ''}
          onSave={handleSavePersona}
          onClose={() => {
            setIsPersonaModalOpen(false);
            setGeneratedPersonaData(null);
          }}
          isSaving={isSavingPersona}
        />
      )}
    </div>
  );

  function goToPrevPage() {
    if (personaAbortControllerRef.current) {
      personaAbortControllerRef.current.abort();
      personaAbortControllerRef.current = null;
      setIsGeneratingPersona(false);
    }

    setCurrentPage(PAGE_TYPES.PRODUCT_LIBRARY_SELECTION);
  }

  async function handleSubmitReview() {
    if (SIMULATE_REVIEW_COMPLETE) {
      setShowReviewResults(true);
      setIsReviewComplete(true);
      setReviewData(SIMULATED_REVIEW_DATA);
      openFindingsView(SIMULATED_REVIEW_DATA);
      setProductDescription(SIMULATED_PRODUCT_DESCRIPTION || productDescription);
      setTitle(SIMULATED_REVIEW_DATA?.title || title);
      setSelectedCategory(SIMULATED_CATEGORY || selectedCategory);
      setSelectedProblemChips(Array.isArray(SIMULATED_PROBLEM_TAGS) ? SIMULATED_PROBLEM_TAGS : []);
      setUploadedImages(SIMULATED_SCREENSHOT_UPLOADS);
      setIsSubmitting(false);

      return;
    }

    if (!isFormValid) {
      return;
    }

    stopJobPolling();

    setIsSubmitting(true);
    setIsReviewComplete(false);
    setReviewData(null);

    try {
      const figmaUserId = authUser?.figma_user_id || userProfile?.figma_user_id || authUser?.figmaUserId || '';
      const isFigmaFlow = figmaUrl && isFigmaURL(figmaUrl) && figmaFramesData?.frames?.length > 0;

      setUploadingImages(true);
      setShowReviewResults(true);

      let formData;

      if (isFigmaFlow) {
        const { frames: figmaFrames, section_name: sectionName } = figmaFramesData;
        const { primingOption, templateLabel, problem, showDeeperInsights } = getCategoryConfig(selectedCategory);
        const userProblem = [productDescription, problem]
          .map(p => (p ?? '').toString().trim())
          .filter(Boolean)
          .join('\n\n');

        const imageBlobs = await downloadFrameImages(figmaFrames);

        formData = buildFigmaReviewFormData({
          imageBlobs,
          frameList: figmaFrames,
          sectionName: sectionName ?? '',
          fileName: sectionName ?? '',
          reviewFrameName: title || '',
          figmaUserId,
          primingOption,
          templateLabel,
          userProblem,
          audienceVal: audience,
          businessGoalVal: businessGoal,
          showDeeperInsights,
          analyticsAttachments: analyticsAttachment || [],
          observedAnalyticsText: observedAnalytics,
          persona: selectedPersona,
        });
      }
      else {
        const isLibraryFlow = uploadedImages.some(img => img.isLibraryImage);

        if (isLibraryFlow) {
          const imageUrlList = uploadedImages.map(img => img.url).filter(Boolean);

          formData = buildGenerateReviewFormData({
            imageUrls: imageUrlList,
            problemText: productDescription,
            figmaUserId,
            category: selectedCategory,
            audience,
            businessGoals: businessGoal,
            analyticsAttachments: analyticsAttachment || [],
            productLibraryData: selectedLibraryFlowData,
            observedAnalyticsText: observedAnalytics,
            persona: selectedPersona,
          });
        }
        else {
          const isVideoFlow = uploadedImages.some(img => img.isVideoFrame);
          let imageFiles = [];

          if (isVideoFlow) {
            imageFiles = videoFrameBlobsRef.current || await Promise.all(
              uploadedImages.map(async (img, index) => {
                const res = await fetch(img.url || img.preview);
                const blob = await res.blob();
                const ext = blob.type ? blob.type.split('/')[1] : 'jpg';

                return new File([blob], `frame_${index + 1}.${ext}`, { type: blob.type || 'image/jpeg' });
              })
            );
          }
          else {
            imageFiles = originalFiles.filter(Boolean);
          }

          formData = buildGenerateReviewFormData({
            imageFiles,
            problemText: productDescription,
            figmaUserId,
            category: selectedCategory,
            figmaUrl: '',
            audience,
            businessGoals: businessGoal,
            analyticsAttachments: analyticsAttachment || [],
            observedAnalyticsText: observedAnalytics,
            persona: selectedPersona,
          });
        }
      }

      latestSubmissionPayloadRef.current = formData;

      const response = await TeamDashboardReviews.submitReview({
        formData,
        workspaceId: selectedWorkspace?.uuid,
      });

      // Clear loading state after backend submission completes
      setUploadingImages(false);

      setReviewData({ extra_data: response.data, final_feedback: [], review_status: 4 });

      const responseJobId = response?.data?.job_id || response?.data?.jobId;
      const responseUuid = response?.data?.uuid;

      // If we have a UUID and we're on the create route, navigate to the review URL
      if (responseUuid && !reviewId) {
        navigate(ROUTES.TEAM_REVIEWS_DETAIL(responseUuid), { replace: true });
      }

      if (responseJobId) {
        jobIdRef.current = responseJobId;
        // Start job polling after 30 seconds
        submitPollTimeoutRef.current = setTimeout(() => {
          submitPollTimeoutRef.current = null;

          if (!isMountedRef.current) {
            return;
          }

          startJobPolling(responseJobId);
        }, 30000);
      }

      // Call history and count APIs after successful submission
      await fetchDesignAuditHistory();
    }
    catch (err) {
      setUploadingImages(false);
      errorReactToastify(
        'Failed to submit review. Please try again.',
        {
          autoClose: 5000,
          position: 'bottom-center',
          hideProgressBar: true,
          style: { backgroundColor: '#FDF0F0', width: '470px' },
        }
      );
      setShowReviewResults(false);
      stopJobPolling();
    }
    finally {
      setIsSubmitting(false);
    }
  }

  // Fetch design audit history
  async function fetchDesignAuditHistory(page = 1, days = 30) {
    try {
      await Promise.all([
        dispatch(getDesignReviewHistoryAction(days, page, PAGE_SIZE, PLATFORM, null, null, selectedWorkspace?.uuid)),
        dispatch(getDesignReviewCountsAction(PLATFORM, selectedWorkspace?.uuid)),
        dispatch(getReviewDataAction()),
      ]);
    }
    catch (error) {
      Sentry.captureException(error);
    }
  }
};

CreateReview.propTypes = {
  onClose: PropTypes.func,
  existingReviewData: PropTypes.object,
  isPublicView: PropTypes.bool,
};

export default CreateReview;
